import { FC, useEffect, useState } from "react";
import { Check, X, ChevronDown, ChevronUp } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
} from "@/components/UI/Dialog/Dialog";
import membershipLight from "@/assets/images/memberships/membership-light.png";
import membershipDark from "@/assets/images/memberships/membership-dark.png";
import img1 from "@/assets/images/journeyImages/final edit.jpg";
import img2 from "@/assets/images/journeyImages/final edit1.jpg";
import img3 from "@/assets/images/journeyImages/final edit2.png";
import img4 from "@/assets/images/journeyImages/final edit3.jpg";
import img5 from "@/assets/images/journeyImages/final edit4.jpg";
import img6 from "@/assets/images/journeyImages/final edit5.jpg";
import img7 from "@/assets/images/journeyImages/final edit6.jpg";
import img8 from "@/assets/images/journeyImages/final edit7.jpg";
import img9 from "@/assets/images/journeyImages/final edit8.jpg";
import img10 from "@/assets/images/journeyImages/final edit9.jpg";
import img11 from "@/assets/images/journeyImages/final edit10.jpg";
import img12 from "@/assets/images/journeyImages/final edit111.jpg";
import img13 from "@/assets/images/journeyImages/final edit12.jpg";
import useIsMobile from "@/custom_hooks/isMobile";

// ─── Types ───────────────────────────────────────────────────────────────────

export type CompareProgramsModalTabKey = "vertinimai" | "kelione";

// ─── Vertinimai tab data ──────────────────────────────────────────────────────

interface VertinimasRow {
  label: string;
  description?: string;
  core: boolean | string;
  apex: boolean | string;
}

interface VertinimasSection {
  title: string;
  rows: VertinimasRow[];
}

const vertinimasSections: VertinimasSection[] = [
  {
    title: "Kardiometabolinė sistema",
    rows: [
      {
        label: "Gliukozės reguliavimas",
        description:
          "Įvertiname, kaip efektyviai organizmas palaiko stabilų cukraus kiekį kraujyje ir ar ląstelės geba panaudoti energiją iš maisto. Tai vertinama pagal genetinę riziką gliukozės apykaitos sutrikimams, atsparumui insulinui, metaboliniam sindromui. Bei pagal tyrimus: gliukozės kraujo tyrimas, insulinas, HbA1c, HOMA-IR.",
        core: true,
        apex: true,
      },
      {
        label: "Nuolatinis gliukozės stebėjimas (CGM)",
        description:
          "Tyrimas, kurį atliekate namuose ir kuris realiuoju laiku stebi gliukozės kiekio pokyčius. Šie duomenys naudojami mūsų gyvensenos specialistės, kad būtų galima personalizuoti jūsų įžvalgas ir rekomendacijas bei optimizuoti medžiagų apykaitos sveikatą.",
        core: false,
        apex: true,
      },
      {
        label: "Riebalų apykaita",
        description:
          "Parodoma, ar riebalai kraujyje palaiko kraujagyslių sveikatą, ar didina aterosklerozės ir širdies ligų riziką. Vertinama Genetinė rizika padidėjusiam cholesteroliui, aterosklerozei, širdies ligoms. Bendras cholesterolio kraujo tyrimas, LDL, HDL, Trigliceridai, ApoB, ApoA, ApoA/ApoB, Lp(a).",
        core: true,
        apex: true,
      },
      {
        label: "Kraujagyslių ir širdies būklė",
        description:
          "Įvertiname širdies gebėjimą aprūpinti organizmą deguonimi ir kaip greitai ji atsistato po krūvio. Vertiname genetinę riziką kraujagyslių standumui, hipertenzijai, sutrikusiai kraujotakai. Stebime HRV. Atliekame kraujo tyrimus: hsCRP, NT-proBNP, Troponinas.",
        core: true,
        apex: true,
      },
    ],
  },
  {
    title: "Fizinis pajėgumas",
    rows: [
      {
        label: "VO₂max",
        description:
          "VO₂Max yra vienas stipriausių ilgaamžiškumo ir bendros sveikatos rodiklių. Šis testas matuoja jūsų maksimalų aerobinio pajėgumo lygį einant ar bėgant ant bėgimo takelio arba naudojant stacionarų dviratį.",
        core: true,
        apex: true,
      },
      {
        label: "Kūno sudėties analizė",
        description:
          "Kūno sudėties analizė parodo ne tik kiek sveriate, bet iš ko susideda jūsų svoris. Ji leidžia įvertinti, ar organizme vyrauja raumenys ar riebalai, ar nėra padidėjusio visceralinių riebalų kiekio, susijusio su širdies ir metabolinėmis ligomis, ir kaip jūsų kūnas realiai reaguoja į mitybą, sportą bei gyvenimo būdo pokyčius.",
        core: true,
        apex: true,
      },
      {
        label: "Jėgos matavimas",
        description:
          "Jėgos matavimas parodo ne tik rankos stiprumą, bet visos nervų-raumenų sistemos būklę. Tai vienas patikimiausių bendros fizinės būklės, atsparumo ligoms ir ilgaamžiškumo rodiklių. Žemesni rezultatai siejami su padidėjusia traumų, kritimų, silpnumo ir net ankstyvo mirtingumo rizika.",
        core: true,
        apex: true,
      },
      {
        label: "Fizinio aktyvumo personalizavimas",
        description:
          "Genetinė analizė leidžia įvertinti, koks fizinis krūvis jūsų organizmui yra optimalus, kaip greitai atsistato raumenys, ar nėra padidėjusios perdegimo ir persitempimo rizikos bei koks treniruočių intensyvumas yra tvarus ilgalaikėje perspektyvoje.",
        core: true,
        apex: true,
      },
    ],
  },
  {
    title: "Nervų ir kognityvinė sistema",
    rows: [
      {
        label: "Streso atsakas",
        description:
          "Nustatoma, kaip organizmas reaguoja į stresą ir ar geba grįžti į pusiausvyrą po apkrovos. Vertinama genetinė rizika sumažėjusiam streso atsparumui, rizika perdegimui, emociniam išsekimui. Remiamasi kraujo tyrimais: Kortizolis, DHEA-S.",
        core: true,
        apex: true,
      },
      {
        label: "Neuromediatorių pusiausvyra",
        description:
          "Įvertinamas biologinis pagrindas nuotaikai, motyvacijai, dėmesiui ir emocinei būsenai. Vertinama genetinė rizika dopamino disbalansui, serotonino disbalansui, rizika motyvacijos sutrikimams. Vertinai kraujo markeriai: vitaminas B12, Folio rūgštis, Homocisteinas.",
        core: true,
        apex: true,
      },
      {
        label: "Smegenų senėjimas",
        description:
          "Vertinama genetinė rizika neurodegeneracijai, kognityviniam silpnėjimui bei rizika atminties sutrikimams.",
        core: true,
        apex: true,
      },
      {
        label: "Miegas ir paros ritmas",
        description:
          "Nustatomas genetinis chronotipas, kuris parodo ar miegas realiai atstato organizmą, ar tik suteikia subjektyvų poilsio jausmą. Oura / Whoop (miego trukmė, gilaus miego fazės), HRV naktį.",
        core: true,
        apex: true,
      },
    ],
  },
  {
    title: "Detoksikacija ir kepenų veikla",
    rows: [
      {
        label: "Detoksikacijos pajėgumas",
        description:
          "Parodoma, kaip intensyviai kepenys dirba ir ar organizmas susitvarko su toksinais. Vertinama genetinė rizika sulėtėjusiai toksinų šalinimo funkcijai bei rizika vaistų kaupimuisi organizme. Atliekami kraujo markeriai kepenų fermenams įvertinti (ALT, AST, ALP, GGT) ir T-Bil, D-Bil, N-Bil.",
        core: true,
        apex: true,
      },
      {
        label: "Metilinimo pusiausvyra",
        description:
          "Parodoma, kaip efektyviai organizmas vykdo pagrindinius biocheminius procesus ir ar geba tinkamai panaudoti B grupės vitaminus energijai, nervų sistemai ir ląstelių atsinaujinimui. Vertinama genetinė rizika sutrikusiai metilinimo funkcijai, rizika prastesnei B grupės vitaminų apykaitai. Įtraukiami kraujo markeriai: Homocisteinas, Folio rūgštis, vitaminas B12.",
        core: true,
        apex: true,
      },
    ],
  },
  {
    title: "Imuninė sistema ir uždegimas",
    rows: [
      {
        label: "Uždegiminis fonas",
        description:
          "Nustatoma, ar organizme vyksta lėtinis žemo lygio uždegimas, spartinantis senėjimą ir ligų vystymąsi. Nustatoma remiantis genetinė rizika padidėjusiam uždegiminiam atsakui bei rizika lėtiniam uždegimui. Matuojami rodikliai: hsCRP, ENG, IL-6.",
        core: true,
        apex: true,
      },
      {
        label: "Imuninė reakcija",
        description:
          "Įvertinama, kaip stipriai imuninė sistema geba apsisaugoti nuo infekcijų ir atsigauti po ligų. Vertinama genetinė rizika nusilpusiai imuninei sistemai bei rizika dažnesnėms infekcijoms. Matuojami markeriai: leukocitų formulė, Vitaminas D, Feritinas.",
        core: true,
        apex: true,
      },
    ],
  },
  {
    title: "Hormonai ir antinksčiai",
    rows: [
      {
        label: "Skydliaukės veikla",
        description:
          "Analizuojama, kaip greitai veikia medžiagų apykaita ir kiek energijos organizmas generuoja kasdienėje veikloje. Remiamasi genetinė rizika hipotirozei bei rizika skydliaukės patologijoms. Atliekami kraujo markeriai: TSH, FT3, FT4, Anti-TPO.",
        core: true,
        apex: true,
      },
      {
        label: "Lytinių hormonų pusiausvyra",
        description:
          "Vertinamas hormoninis fonas, kuris lemia raumenų būklę, riebalų pasiskirstymą, libido ir vaisingumą. Vertinama genetinė rizika testosterono disbalansui ir rizika estrogenų disbalansui. Atliekami kraujo tyrimai: Testosteronas, Estradiolis, SHBG.",
        core: true,
        apex: true,
      },
      {
        label: "Antinksčių apkrova",
        description:
          "Analizuojama, ar organizmas nėra nuolat \u201estreso režime\u201c ir ar hormoninė sistema nepersidirbusi. Vertinama genetinė rizika padidėjusiam kortizolio lygiui ir hormoniniam nuovargiui. Atliekami tyrimai: Kortizolis, DHEA-S.",
        core: true,
        apex: true,
      },
    ],
  },
  {
    title: "Maisto papildai ir vaistai",
    rows: [
      {
        label: "Maistinių medžiagų pasisavinimas",
        description:
          "Įvertinama kaip efektyviai organizmas įsisavina vitaminus ir mikroelementus bei jų rezervas. Remiamasi genetinė rizika prastesniam pasisavinimui ir rizika mikroelementų trūkumams. Vertinami tyrimai: Feritinas, Vitaminas B12, Cinkas, Kalis, Natris, Chloras, Vitaminas D.",
        core: true,
        apex: true,
      },
      {
        label: "Vaistų tolerancija – farmakogenetika",
        description:
          "Nustatomos medžiagos bei vaistai, kuriuos organizmas pasisavina ir kurių pasisavinamumas yra prastesnis, todėl rekomenduojama ieškoti analogų konkrečiam vaistui, nes nebus užtikrintas jo veiksingumas. Genetinė rizika lėtam vaistų skaidymui. Šalutinių poveikių analizė.",
        core: false,
        apex: true,
      },
      {
        label: "Farmacininko konsultacija",
        description:
          "Nepriklausomas farmacininkas kiekvienam pacientui pagal turimus duomenis rekomenduoja personalizuotus maisto papildus, prižiūri vartojimo eigoje, koreguoja protokolą pagal individualų poreikį. Taip pat, farmacininkas peržiūri vaistų suderinamumą ir suteikia rekomendacijas dėl efektyviausio vartojimo.",
        core: false,
        apex: true,
      },
    ],
  },
  {
    title: "Elgesys ir priklausomybės",
    rows: [
      {
        label: "Priklausomybės",
        description:
          "Įvertinamas polinkis atlygio siekiančiam elgesiui ir vertinamos savikontrolės ribos. Analizuojami genetiniai polinkiai į alkoholio, cukraus, nikotino, kokaino, opioidų vartojimo rizikas.",
        core: true,
        apex: true,
      },
      {
        label: "Asmenybė ir motyvacija",
        description:
          "Parodo, kaip žmogus priima sprendimus ir laikosi rekomendacijų pagal tai, kas užkoduota jo genetikoje. Pavyzdžiui, ryžtas ir baimė priimti sprendimus, darboholizmas ar pervargimo grėsmė, kritikos priėmimas ir lankstumas.",
        core: true,
        apex: true,
      },
    ],
  },
  {
    title: "Paveldimų ligų rizika",
    rows: [
      {
        label: "Gydytojo genetiko konsultacija",
        description:
          "Klinikinė genetinių rezultatų interpretacija, rizikų suskirstymas į aktualias ir stebėtinas ateityje bei individualaus prevencinio plano sudarymas pagal nustatytas genetines rizikas.",
        core: false,
        apex: true,
      },
      {
        label: "Klinikinis genomo tyrimas",
        description:
          "Vertinama genetinė rizika paveldėti: širdies ir kraujagyslių ligas (genetiniai sindromai), neurologines ir neurodegeneracines ligas, endokrininių ligų genetines formas, onkologinių ligų paveldimus sindromus, imuninės sistemos genetinius sutrikimus, metabolinių ligų paveldimas formas.",
        core: false,
        apex: true,
      },
      {
        label: "Retų ligų identifikavimas",
        description:
          "Nustatomos paveldimos retos ligos: jungiamojo audinio ligos, paveldimos kardiomiopatijos ir aritmijos, retos metabolinės ligos, paveldimos neurologinės ir neuromuskulinės ligos, pirminiai imunodeficitai.",
        core: false,
        apex: true,
      },
      {
        label: "Šeimos planavimo strategija",
        description:
          "Vertinama genetinės rizikos perdavimo vaikams tikimybė, tikslinių genetinių tyrimų poreikis partneriui, galimos paveldimos būklės planuojant šeimą.",
        core: false,
        apex: true,
      },
    ],
  },
  {
    title: "Gyvensenos programa",
    rows: [
      {
        label: "Gyvensenos specialistės konsultacijos",
        description:
          "Individuali konsultacija, kurios metu diagnostiniai duomenys sujungiami su kasdieniais sprendimais, vertinami gyvenimo būdo veiksniai, turintys didžiausią įtaką savijautai, energijai, miegui, stresui, kūno sudėčiai ir ilgalaikėms rizikoms.",
        core: true,
        apex: true,
      },
      {
        label: "Gydytojo konsultacija",
        description:
          "Klinikinė konsultacija, kurios metu įvertinami visi kraujo tyrimų rezultatai ir jų tarpusavio sąsajos, nustatomos prioritetinės korekcijos sritys.",
        core: true,
        apex: true,
      },
      {
        label: "12 savaičių gyvensenos korekcijos programa",
        description:
          "Struktūruota intensyvi programa, kurios metu diagnostiniai duomenys paverčiami realiais, pamatuojamais veiksmais – formuojami ilgalaikiai įpročiai mitybos, judėjimo, miego ir streso srityse, atsižvelgiant į individualius biologinius skirtumus.",
        core: true,
        apex: true,
      },
      {
        label: "Elgesio pokyčių ir įpročių formavimas",
        description:
          "Įvertinami individualūs elgesio modeliai, motyvacijos mechanizmai ir streso reakcijos, kad rekomendacijos būtų realiai pritaikomos kasdieniam gyvenimui. Ši sistema padeda paversti diagnostinius duomenis ilgalaikiais įpročiais, užtikrinant, kad pokyčiai būtų ne laikini, o integruoti į gyvenimo būdą.",
        core: true,
        apex: true,
      },
    ],
  },
  {
    title: "Ilgalaikė priežiūra",
    rows: [
      {
        label: "Pacientų vadyba",
        description:
          "Bazinis paciento kelio koordinavimas, užtikrinantis, kad visi tyrimai ir konsultacijos vyktų logiška seka ir pacientas aiškiai suprastų, kas ir kodėl atliekama.",
        core: true,
        apex: true,
      },
      {
        label: "Asmeninė pacientų vadyba",
        description:
          "Asmeninė klinikinė vadyba – tai pilnas paciento kelio perėmimas: nuo registracijų pas kitus specialistus ar įstaigas ir terminų derinimo iki ilgalaikio stebėjimo ir sveikatos strategijos koordinavimo.",
        core: false,
        apex: true,
      },
      {
        label: "Pakartotiniai kraujo tyrimai",
        description:
          "Po 12 savaičių programos atliekami pakartotiniai svarbiausi kraujo rodikliai, siekiant įvertinti realius biologinius pokyčius ir koreguoti tolimesnius veiksmus.",
        core: true,
        apex: true,
      },
      {
        label: "Pakartotinis VO₂max matavimas",
        description:
          "Atliekamas pakartotinis VO₂max matavimas ir įvertinama dinamika.",
        core: false,
        apex: true,
      },
      {
        label: "Pakartotinė kūno sudėties analizė",
        description: "Pakartojama kūno sudėties analizė, įvertinamas pokytis.",
        core: false,
        apex: true,
      },
      {
        label: "Pakartotinis jėgos matavimas",
        description: "Pakartojamas jėgos matavimas ir vertinama dinamika.",
        core: false,
        apex: true,
      },
    ],
  },
];

// ─── Kelionė tab data ─────────────────────────────────────────────────────────

interface KelionėRow {
  title: string;
  description: string;
  timeLabel?: string;
  core: boolean;
  apex: boolean;
  img: string;
}

const kelioneRows: KelionėRow[] = [
  {
    title: "Pradinis skambutis su pacientų vadybininke",
    description:
      "Tai pirmas kontaktas, kurio tikslas - užtikrinti, kad pacientas suprastų visą kelionę dar prieš pradedant diagnostiką.",
    timeLabel: "0 diena",
    core: true,
    apex: true,
    img: img1,
  },
  {
    title: "Gyvensenos anamnezės surinkimas",
    description:
      "Šie duomenys leidžia suprasti ne tik biologinę, bet ir elgesio pusę - kaip žmogus gyvena realybėje esamuoju metu.",
    timeLabel: "1 diena",
    core: true,
    apex: true,
    img: img2,
  },
  {
    title: "Įvadinė konsultacija su gyvensenos specialiste",
    description:
      "Jos metu tikslinami pagrindiniai tikslai, aptariama esama sveikatos situacija. Parenkami reikalingi papildomi tyrimai, sudaromas individualus diagnostikos planas ir susitariamas tolimesnių patikrinimų dažnis.",
    timeLabel: "1 diena",
    core: true,
    apex: true,
    img: img3,
  },
  {
    title: "Diagnostikos diena",
    description:
      "Mėginių tyrimams pridavimas, VO₂max, kūno sudėtis, jėgos matavimas. Šie duomenys sudaro objektyvų biologinį „starto tašką“, kuris vėliau naudojamas pokyčiams matuoti.",
    timeLabel: "Po savaitės",
    core: true,
    apex: true,
    img: img4,
  },
  {
    title: "Kraujo tyrimų vertinimas su šeimos gydytoja",
    description:
      "Nustatomos prioritetinės rizikos ir būklės, kurios turi didžiausią įtaką sveikatai bei gyvensenos aptarimas.",
    timeLabel: "Po 2 savaičių",
    core: true,
    apex: true,
    img: img5,
  },
  {
    title: "Farmacininko konsultacija",
    description:
      "Sudaromas individualus papildų protokolas pagal kraujo ir funkcinės diagnostikos rezultatus.",
    timeLabel: "Po 2 savaičių",
    core: false,
    apex: true,
    img: img6,
  },
  {
    title: "Gyvensenos genetikos konsultacija",
    description:
      "Aptariami genetiniai duomenys: kaip genetika veikia mitybą, sportą, stresą, miegą ir atsistatymą.",
    timeLabel: "Po 4-6 savaičių",
    core: true,
    apex: true,
    img: img7,
  },
  {
    title: "Gyvensenos konsultacijos, plano korekcijos",
    description:
      "Korekcijos pagal realią patirtį: kas veikia, kas per sunku, kas reikalauja adaptacijos bei trumpi pasitikrinimai.",
    timeLabel: "Po 4-6 savaičių",
    core: true,
    apex: true,
    img: img8,
  },
  {
    title: "Klinikinio genomo tyrimo rezultatų aptarimas su gydytoja genetike",
    description:
      "Išsamus paveldimų ligų, onkologinių sindromų ir prevencijos aptarimas.",
    timeLabel: "Po 8-9 savaičių",
    core: false,
    apex: true,
    img: img9,
  },
  {
    title: "Gyvensenos konsultacija",
    description:
      "Visa informacija sujungiama į vieną aiškią intervencijos strategiją: mityba, judėjimas, miegas, stresas.",
    timeLabel: "Po 8-9 savaičių",
    core: true,
    apex: true,
    img: img10,
  },
  {
    title: "Pakartotiniai tyrimai",
    description:
      "Pakartotinis kraujo tyrimų atlikimas, rezultatų peržiūra bei įvertinimas.",
    timeLabel: "Po 12 savaičių",
    core: true,
    apex: true,
    img: img12,
  },
  {
    title: "Pakartotiniai matavimai",
    description:
      "Pakartotiniai VO₂max, kūno sudėties, jėgos matavimai bei rezultatų įvertinimas.",
    timeLabel: "Po 12 savaičių",
    core: false,
    apex: true,
    img: img11,
  },
  {
    title: "Kasmėnesiniai palaikomieji gyvensenos susitikimai",
    description:
      "Asmeninė gyvensenos specialistės prežiūra, siekiant sukurti ilgalaikį pokytį.",
    timeLabel: "Po programos",
    core: false,
    apex: true,
    img: img13,
  },
];

const groupKelioneRowsByTimeLabel = (rows: KelionėRow[]) => {
  const groups: { timeLabel: string; rows: KelionėRow[] }[] = [];
  for (const row of rows) {
    const timeLabel = row.timeLabel ?? "";
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.timeLabel === timeLabel) {
      lastGroup.rows.push(row);
      continue;
    }
    groups.push({ timeLabel, rows: [row] });
  }
  return groups;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const CheckIcon: FC<{ yes: boolean }> = ({ yes }) => (
  <div
    className={`flex h-5 w-5 items-center justify-center rounded-full shadow-[var(--shadow-sm)] md:h-6 md:w-6 ${
      yes ? "bg-[var(--color-green)]" : "bg-danger"
    }`}
  >
    {yes ? (
      <Check size={12} className="text-white md:size-[14px]" />
    ) : (
      <X size={12} className="text-white md:size-[14px]" />
    )}
  </div>
);

const CellValue: FC<{ value: boolean | string }> = ({ value }) => {
  if (typeof value === "string") {
    return (
      <span className="text-center text-sm leading-[1.5] text-[#444] md:text-[16px]">
        {value}
      </span>
    );
  }
  return <CheckIcon yes={value} />;
};

const CollapsibleRow: FC<{ row: VertinimasRow }> = ({ row }) => {
  const [open, setOpen] = useState(false);
  const hasDescription = Boolean(row.description);

  return (
    <div className="grid w-full grid-cols-[2fr_1fr_1fr] items-center border-b border-dashed border-[#F4F4F4] md:grid-cols-[2fr_1fr_1fr]">
      {/* Label column */}
      <div className="flex min-w-0 flex-col px-4 py-3 md:px-0 md:py-[20px] md:pr-6 md:pl-[20px]">
        <button
          onClick={() => hasDescription && setOpen((p) => !p)}
          className={`flex items-center gap-1.5 text-left text-sm leading-[1.5] text-[#444] md:text-[16px] ${hasDescription ? "cursor-pointer" : "cursor-default"}`}
        >
          {row.label}
          {hasDescription &&
            (open ? (
              <ChevronUp size={16} className="mt-0.5 shrink-0 text-[#888]" />
            ) : (
              <ChevronDown size={16} className="mt-0.5 shrink-0 text-[#888]" />
            ))}
        </button>
        {open && row.description && (
          <p className="mt-1 text-xs leading-[1.4] text-[#888] md:text-[14px]">
            {row.description}
          </p>
        )}
      </div>

      {/* Core column */}
      <div className="flex items-center justify-center border-l border-dashed border-[#F4F4F4] py-3 md:py-[20px]">
        <CellValue value={row.core} />
      </div>

      {/* Apex column */}
      <div className="flex items-center justify-center py-3 md:py-[20px]">
        <CellValue value={row.apex} />
      </div>
    </div>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────

interface CompareProgramsModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  openToTab?: CompareProgramsModalTabKey;
}

const CompareProgramsModal: FC<CompareProgramsModalProps> = ({
  isOpen,
  onOpenChange,
  openToTab,
}) => {
  const [activeTab, setActiveTab] =
    useState<CompareProgramsModalTabKey>("vertinimai");

  const isMobile = useIsMobile();
  const kelioneGroups = groupKelioneRowsByTimeLabel(kelioneRows);

  useEffect(() => {
    if (!isOpen) return;
    if (!openToTab) return;
    setActiveTab(openToTab);
  }, [isOpen, openToTab]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        showClose={false}
        className="bg-paper sm:rounded-control sm:border-surface h-full w-full max-w-full overflow-y-auto overscroll-contain rounded-none border-0 p-0 shadow-lg sm:inset-0 sm:top-0 sm:left-0 sm:m-auto sm:flex sm:h-[92dvh] sm:max-h-[92dvh] sm:w-[min(1500px,96vw)] sm:max-w-[min(1500px,96vw)] sm:translate-x-0 sm:translate-y-0 sm:flex-col sm:border"
      >
        <div className="flex h-full flex-col">
          <div className="flex-1">
            <div className="relative sticky top-0 z-10 border-b border-dashed border-[#F4F4F4] bg-white pt-10 pb-4 md:pt-8 md:pb-6">
              <DialogClose
                aria-label="Close"
                className="absolute top-4 right-4 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/80 text-[#444] shadow-[var(--shadow-sm)] backdrop-blur transition hover:bg-white focus:outline-none"
              >
                <X size={18} />
              </DialogClose>
              <div className="flex items-center px-4 md:px-6">
                <h2 className="text-[28px] leading-[1.2] font-normal text-[#181818] md:text-[44px]">
                  Palyginti narystes
                </h2>
              </div>

              {isMobile ? (
                <div className="mt-4 flex flex-col gap-4">
                  <div className="grid grid-cols-[2fr_1fr_1fr] items-center">
                    <div />
                    <div className="col-start-2 flex w-full shrink-0 justify-center border-l border-dashed border-[#F4F4F4]">
                      <div className="flex flex-col-reverse items-center justify-center gap-2 px-4 py-3">
                        <span className="text-sm leading-[1.5] text-[#444]">
                          Core
                        </span>
                        <img
                          src={membershipLight}
                          alt="Core"
                          className="h-5 w-auto object-contain"
                        />
                      </div>
                    </div>
                    <div className="col-start-3 flex w-full shrink-0 justify-center">
                      <div className="flex flex-col-reverse items-center justify-center gap-2 px-4 py-3">
                        <span className="text-sm leading-[1.5] text-[#444]">
                          Apex
                        </span>
                        <img
                          src={membershipDark}
                          alt="Apex"
                          className="h-5 w-auto object-contain"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex min-w-0 items-center">
                    <div className="bg-surface border-surface flex w-full items-center gap-[5px] rounded-full border p-[5px]">
                      {(
                        [
                          { key: "vertinimai", label: "Vertinimai" },
                          { key: "kelione", label: "Kelionė" },
                        ] as {
                          key: CompareProgramsModalTabKey;
                          label: string;
                        }[]
                      ).map(({ key, label }) => (
                        <button
                          key={key}
                          onClick={() => setActiveTab(key)}
                          className={`h-10 flex-1 rounded-full px-4 text-sm leading-[1.44] font-normal transition-all ${
                            activeTab === key
                              ? "border-surface border bg-white text-[#181818] shadow-sm"
                              : "text-[#181818]"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={`mt-4 grid items-center ${
                    activeTab === "kelione"
                      ? "grid-cols-[2fr_1fr_1fr] md:grid-cols-[2fr_1fr_1fr]"
                      : "grid-cols-[2fr_1fr_1fr] md:grid-cols-[2fr_1fr_1fr]"
                  } md:mt-4 md:gap-x-0`}
                >
                  <div className="col-span-3 flex min-w-0 items-center px-4 md:col-span-1 md:px-[20px]">
                    <div className="bg-surface border-surface flex w-full items-center gap-[5px] rounded-full border p-[5px] md:w-auto">
                      {(
                        [
                          { key: "vertinimai", label: "Vertinimai" },
                          { key: "kelione", label: "Kelionė" },
                        ] as {
                          key: CompareProgramsModalTabKey;
                          label: string;
                        }[]
                      ).map(({ key, label }) => (
                        <button
                          key={key}
                          onClick={() => setActiveTab(key)}
                          className={`h-10 flex-1 rounded-full px-4 text-sm leading-[1.44] font-normal transition-all md:h-[48px] md:flex-initial md:px-[26px] md:text-[16px] ${
                            activeTab === key
                              ? "border-surface border bg-white text-[#181818] shadow-sm"
                              : "text-[#181818]"
                          }`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="col-start-2 flex w-full shrink-0 justify-center border-l border-dashed border-[#F4F4F4] md:col-start-auto">
                    <div className="flex items-center justify-center gap-2 px-4 py-3 md:gap-2 md:px-5 md:py-3.5">
                      <span className="text-sm leading-[1.5] text-[#444] md:text-[16px]">
                        Core
                      </span>
                      <img
                        src={membershipLight}
                        alt="Core"
                        className="h-5 w-auto object-contain md:h-6"
                      />
                    </div>
                  </div>

                  <div className="col-start-3 flex w-full shrink-0 justify-center md:col-start-auto">
                    <div className="flex items-center justify-center gap-2 px-4 py-3 md:gap-2 md:px-5 md:py-3.5">
                      <span className="text-sm leading-[1.5] text-[#444] md:text-[16px]">
                        Apex
                      </span>
                      <img
                        src={membershipDark}
                        alt="Apex"
                        className="h-5 w-auto object-contain md:h-6"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              {activeTab === "vertinimai" && (
                <div className="flex flex-col">
                  {vertinimasSections.map((section) => (
                    <div key={section.title} className="flex flex-col">
                      <div className="bg-surface grid w-full grid-cols-[2fr_1fr_1fr] items-stretch rounded-[10px] md:grid-cols-[2fr_1fr_1fr]">
                        <div className="flex min-w-0 items-center px-4 py-4 md:px-[20px] md:py-[18px] md:pr-6">
                          <span className="text-sm leading-[1.5] font-medium text-[#181818] md:text-[16px]">
                            {section.title}
                          </span>
                        </div>
                        <div />
                        <div />
                      </div>
                      {section.rows.map((row) => (
                        <CollapsibleRow key={row.label} row={row} />
                      ))}
                    </div>
                  ))}
                </div>
              )}

              {activeTab === "kelione" && (
                <div className="flex flex-col">
                  <div className="bg-surface mb-4 grid w-full grid-cols-[2fr_1fr_1fr] md:mb-[20px]">
                    <div className="flex min-w-0 items-center px-4 py-4 md:px-[20px] md:py-[18px] md:pr-6">
                      <span className="text-ink min-w-0 text-sm leading-[1.5] font-medium md:text-[16px]">
                        Klinikiniai kontaktiniai taškai
                      </span>
                    </div>

                    <div className="border-surface flex items-center justify-center border-l border-dashed py-2 md:py-[10px]">
                      <span className="text-muted text-sm leading-[1.5] md:text-[16px]">
                        12
                      </span>
                    </div>
                    <div className="flex items-center justify-center py-2 md:py-[10px]">
                      <span className="text-muted text-sm leading-[1.5] md:text-[16px]">
                        15
                      </span>
                    </div>
                  </div>

                  {kelioneGroups.map((group, groupIdx) =>
                    group.rows.map((row, rowIdx) => {
                      const isLastRowInGroup = rowIdx === group.rows.length - 1;
                      const isLastGroup = groupIdx === kelioneGroups.length - 1;
                      const isLastOverall = isLastGroup && isLastRowInGroup;
                      const showDivider = isLastRowInGroup && !isLastOverall;

                      return (
                        <div
                          key={`${groupIdx}-${rowIdx}-${row.title}`}
                          className={`border-surface grid w-full grid-cols-[2fr_1fr_1fr] items-stretch md:grid-cols-[2fr_1fr_1fr] ${
                            showDivider ? "border-b border-dashed" : ""
                          }`}
                        >
                          {isMobile ? (
                            <>
                              <div className="hidden min-w-0 px-4 py-4 md:flex md:items-start">
                                <span
                                  className={`text-ink text-sm leading-[1.5] font-semibold ${
                                    rowIdx === 0 ? "" : "opacity-0"
                                  }`}
                                  aria-hidden={rowIdx !== 0}
                                >
                                  {group.timeLabel}
                                </span>
                              </div>
                              <div className="flex flex-col gap-[5px] p-4">
                                <div className="flex gap-1">
                                  <img
                                    src={row.img}
                                    alt={row.title}
                                    className="h-6 w-6 rounded-[5px] object-cover"
                                  />
                                  {isMobile &&
                                  rowIdx === 0 &&
                                  group.timeLabel ? (
                                    <p className="text-muted flex w-fit items-center justify-center rounded-[5px] bg-[#f5f5f5] px-2 py-1 text-center text-xs leading-none font-semibold">
                                      {group.timeLabel}
                                    </p>
                                  ) : null}
                                </div>
                                <div className="flex flex-col gap-[5px]">
                                  <span className="text-ink text-sm leading-[1.4] font-semibold md:text-base">
                                    {row.title}
                                  </span>
                                  {row.description ? (
                                    <p className="text-muted text-xs leading-[1.5] md:text-sm">
                                      {row.description}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="flex w-full flex-row gap-4 p-4">
                              <div className="flex w-auto shrink-0">
                                <img
                                  src={row.img}
                                  alt={row.title}
                                  className="h-32.5 w-27.5 shrink-0 rounded-[5px] object-cover"
                                />
                              </div>
                              <div className="flex flex-col justify-center">
                                {rowIdx === 0 && group.timeLabel && (
                                  <div className="text-muted mb-1 flex w-fit items-center justify-center rounded-[5px] bg-[#f5f5f5] px-2 py-1 text-sm leading-[1.5] font-semibold">
                                    {group.timeLabel}
                                  </div>
                                )}
                                <div className="flex flex-col gap-[5px]">
                                  <span className="text-ink text-sm leading-[1.4] font-semibold md:text-base">
                                    {row.title}
                                  </span>
                                  {row.description ? (
                                    <p className="text-muted text-xs leading-[1.5] md:text-sm">
                                      {row.description}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="border-surface flex items-center justify-center border-l border-dashed py-4 md:py-[20px]">
                            <CheckIcon yes={row.core} />
                          </div>
                          <div className="flex items-center justify-center py-4 md:py-[20px]">
                            <CheckIcon yes={row.apex} />
                          </div>
                        </div>
                      );
                    }),
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompareProgramsModal;
